import Leaflet, { LatLngExpression, MarkerCluster } from 'leaflet'
import 'leaflet.markercluster'
import 'leaflet.markercluster.freezable'

import React, { useEffect, useRef, useState } from 'react'
import { MapContainer, TileLayer, Marker, ZoomControl } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { Geolocation } from '@capacitor/geolocation'

import '../styles/components/Map.scss'
import target from '../styles/image/target-3696.png'

import type { POI } from '../models/POI'
import { POIType } from '../models/POI'
import {
  iconRestaurant,
  iconAFaire,
  iconHebergement,
  iconAVoir,
  iconProduit,
  iconService
} from '../utils/MarkerIcons'
import { useSelector, useDispatch } from 'react-redux'
import type { RootState } from '../stores/globalStore'
import * as L from 'leaflet'
import {
  selectPOIIdentifier,
  toggleFilters,
  updateFilters,
  updateMapState
} from '../features/mapSlice'

import MarkerClusterGroup from 'react-leaflet-cluster/lib/index'
import { POIDetail } from './POIDetail'
import { getPoiColor } from '../utils/poiUtils'
import type { MapSettings, MapState } from '../models/Map'
import type { Tenant } from '../models/Tenant'
import { LocationMarker } from './LocationMarker'
import { setActiveTenant, setTenantList } from '../features/tenantSlice'
import { injectShadowCSS } from '../utils/botUtils'
import { setCSSInjected } from '../features/botSlice'
import { MapFilter } from './MapFilter'
import { IonImg } from '@ionic/react'
import ReactDOM from 'react-dom/client'
import {
  CurrentSubView,
  RetorikEvent,
  useUtilsStore,
  useLocaleStore
} from '@davi-ai/retorik-framework'

import translation from '../translations/map.json'

Leaflet.Icon.Default.imagePath = '../node_modules/leaflet'

Leaflet.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
})

interface MapProps {
  activeTenant?: string
  tenantList: { [key: string]: Tenant }
  userLocation?: LatLngExpression
  settings?: MapSettings
}

const MapDisplay = (props?: MapProps | undefined): JSX.Element => {
  const pois = useSelector((state: RootState) => state.poi.poiList)
  const dispatch = useDispatch()
  const locale = useLocaleStore((state) => state.locale)
  const retorikEvent = useUtilsStore((state) => state.retorikEvent)
  const currentSubView = useUtilsStore((state) => state.currentSubView)
  const botCSSInjected: boolean = useSelector(
    (state: RootState) => state.bot.CSSInjected
  )

  const [currentUserPos, setCurrentUserPos] = useState<
    LatLngExpression | undefined
  >()
  const currentFilter = useSelector(
    (state: RootState) => state.map.currentFilter
  )
  const mapState = useSelector((state: RootState) => state.map.mapState)
  const selectedPOIIdentifier = useSelector(
    (state: RootState) => state.map.selectedPOIIdentifier
  )

  const [activeTenant] = useState<string>(
    useSelector((state: RootState) => state.tenant.activeTenant)
  )
  const [tenantList] = useState<{ [key: string]: Tenant }>(
    useSelector((state: RootState) => state.tenant.tenantList)
  )

  const loading = useSelector((state: RootState) => state.map.loading)

  const [aroundMeDistance, setAroundMeDistance] = useState<number>(15000)
  const markersRef = useRef<Array<{ marker: Leaflet.Marker<any>; id: string }>>(
    []
  )

  const mapRef = useRef<Leaflet.Map>()
  const [selectedPOI, setSelectedPOI] = useState<POI | undefined>()
  const [mapSettings] = useState<MapSettings | undefined>(props?.settings)
  const markerDict: { [name: string]: L.Icon } = {
    restaurant: iconRestaurant,
    'a-faire': iconAFaire,
    'a-voir': iconAVoir,
    hebergement: iconHebergement,
    produit: iconProduit,
    service: iconService
  }

  useEffect(() => {
    if (props && props.activeTenant) {
      dispatch(setActiveTenant({ activeTenant: props.activeTenant }))
    }
    if (props && props.tenantList) {
      dispatch(setTenantList({ tenantList: props.tenantList }))
    }
    if (props && props.userLocation) {
      setCurrentUserPos(props.userLocation)
    }
  }, [props])

  useEffect(() => {
    // Show filters
    console.log('Show Filters')
    dispatch(toggleFilters({ showFilters: true }))
    // Handle old state

    window.setTimeout(function () {
      if (mapRef && mapRef.current) {
        mapRef.current.whenReady(() => {
          console.log('MAP READY')
          if (mapRef && mapRef.current) mapRef.current.invalidateSize()
        })
      }

      console.log('MAPSTATE RETREIVE', mapState, mapRef.current)
      if (mapState && mapState.location && mapState.zoom) {
        if (mapRef.current) {
          mapRef.current.setView(mapState.location, mapState.zoom)
          mapRef.current.invalidateSize()
        }
      } else {
        // first time display map
        aroundMe()
      }
    }, 250)

    // Inject CSS to shadow if not already done
    if (!botCSSInjected) {
      injectShadowCSS()
      dispatch(setCSSInjected({ CSSInjected: true }))
    }

    if (!props || !props.userLocation) {
      Geolocation.getCurrentPosition()
        .then((res) => {
          if (res) {
            console.log(res.coords.latitude + '/' + res.coords.longitude)
            if (mapRef.current) {
              // mapRef.current.flyTo([res.coords.latitude, res.coords.longitude], 18)
              const center: LatLngExpression = {
                lat: res.coords.latitude,
                lng: res.coords.longitude
              }
              if (center !== currentUserPos) setCurrentUserPos(center)
            }
          }
        })
        .catch((error) => {
          console.log(error)
        })
    }

    // On unmount : hide filters
    return (): void => {
      console.log('DESTROY MAP COMPONENT')
      // save map state
      if (markersRef.current && botCSSInjected) {
        const saveMapState: MapState = {
          zoom: mapRef.current?.getZoom(),
          location: mapRef.current?.getCenter()
        }
        console.log('MAPSTATE SAVE', saveMapState)
        dispatch(
          updateMapState({
            mapState: saveMapState
          })
        )
      }
      dispatch(toggleFilters({ showFilters: false }))
      console.log('Hide Filters')
      setSelectedPOI(undefined)
      dispatch(selectPOIIdentifier({ selectedPOIIdentifier: '' }))
      dispatch(setCSSInjected({ CSSInjected: false }))
    }
  }, [])

  useEffect(() => {
    console.log(
      'currentSubView, retorikEvent',
      currentSubView,
      retorikEvent,
      mapRef,
      mapState
    )
    if (
      retorikEvent === RetorikEvent.DetailViewOpen &&
      currentSubView === CurrentSubView.custom
    ) {
      if (mapState && mapState.location && mapState.zoom) {
        if (mapRef.current) {
          console.log('Retreive map state', mapState)
          window.setTimeout(function () {
            if (
              mapRef.current &&
              mapState &&
              mapState.location &&
              mapState.zoom
            ) {
              /* mapRef.current.panTo(mapState.location)
              mapRef.current.setZoom(mapState.zoom) */
              mapRef.current.setView(mapState.location, mapState.zoom)

              mapRef.current.whenReady(() => {
                console.log('MAP READY')
                if (mapRef && mapRef.current) mapRef.current.invalidateSize()
              })
            }
          }, 250)
        }
      }
      if (mapRef.current) {
        mapRef.current.invalidateSize()
      }
    }
    if (
      retorikEvent === RetorikEvent.DetailViewClose &&
      currentSubView === CurrentSubView.none
    ) {
      if (markersRef.current) {
        const saveMapState: MapState = {
          zoom: mapRef.current?.getZoom(),
          location: mapRef.current?.getCenter()
        }
        console.log('MAPSTATE SAVE', saveMapState)
        if (
          saveMapState.zoom !== undefined &&
          saveMapState.location !== undefined
        )
          dispatch(
            updateMapState({
              mapState: saveMapState
            })
          )
      }
    }
  }, [retorikEvent])

  useEffect(() => {
    const aroundMeDistance: number | undefined =
      tenantList[activeTenant]?.aroundMeDistance
    console.log('Arround me distance : ', aroundMeDistance)
    if (aroundMeDistance) {
      setAroundMeDistance(aroundMeDistance)
    }
  }, [tenantList, activeTenant])

  useEffect(() => {
    console.log('selectedPOI', selectedPOI)

    // remove Marker HighLight
    markersRef.current.forEach((m) => {
      if (m && m.marker) {
        const element = m.marker.getElement()
        if (element) {
          element.classList.remove('markerSelected')
        }
      }
    })
    if (selectedPOI) {
      const selectedMarker = markersRef.current.find(
        (m) => m.id === selectedPOI.contentItemId
      )
      console.log('selectedMarker', selectedMarker)

      if (selectedMarker) {
        // Zoom and center to marker
        /* if (mapRef.current)
          mapRef.current.flyTo(selectedMarker.marker.getLatLng(), 18) */
        const element = selectedMarker.marker.getElement()
        // Highlight marker
        if (element) {
          element.classList.add('markerSelected')
        }
      }
    }
  }, [selectedPOI])

  useEffect(() => {
    // remove Marker HighLight
    console.log(mapRef.current)
    if (mapRef.current) {
      console.log('zoom ==>' + mapRef.current.getZoom())
      mapRef.current.removeEventListener('click')
      mapRef.current.addEventListener('click', () => {
        markersRef.current.forEach((m) => {
          if (m && m.marker) {
            const element = m.marker.getElement()
            if (element) {
              element.classList.remove('markerSelected')
            }
            setSelectedPOI(undefined)
          }
        })
      })
    }
  }, [mapRef.current])

  useEffect(() => {
    console.log('UseEffect selectedPOI', selectedPOIIdentifier, markersRef)
    if (selectedPOIIdentifier !== '') {
      const selectedPOIObj = pois.find(
        (p) => p.contentItemId === selectedPOIIdentifier
      )
      const selectedMarker = markersRef.current.find(
        (m) => m.id === selectedPOIIdentifier
      )
      console.log('UseEffect selectedPOI selectedMarker', selectedMarker)

      if (
        selectedMarker &&
        selectedPOIObj &&
        selectedPOIObj.Location &&
        mapRef.current
      ) {
        dispatch(
          updateMapState({ mapState: { location: undefined, zoom: undefined } })
        )
        mapRef.current.flyTo(selectedPOIObj.Location, 18)
        mapRef.current.on('zoomend', function () {
          console.log('on zoomend')
          setSelectedPOI(selectedPOIObj)
          selectedMarker.marker.openPopup(selectedPOIObj?.Location)
          dispatch(selectPOIIdentifier({ selectedPOIIdentifier: '' }))
          if (mapRef.current) mapRef.current.removeEventListener('zoomend')
        })
      } else if (selectedPOIObj) {
        dispatch(
          updateFilters({ currentFilter: [selectedPOIObj.type as POIType] })
        )
      }
    }
  }, [selectedPOIIdentifier, currentFilter])

  const markerClickHandler = (
    _e: Leaflet.LeafletMouseEvent,
    poi: POI
  ): void => {
    setSelectedPOI(poi)
  }

  const closePoi = (): void => {
    setSelectedPOI(undefined)
  }

  function aroundMe(): void {
    console.log('aroundMe')
    if (props?.userLocation) {
      if (props.tenantList && props.activeTenant) {
        let tenantName = props.activeTenant
        if (tenantName.indexOf('asklea_') > -1) {
          tenantName = tenantName.replaceAll('asklea_', '')
        }
        handlerAroundMe(props.userLocation as Leaflet.LatLngLiteral)
        return
      } else {
        if (tenantList && tenantList[activeTenant]) {
          handlerAroundMe(props.userLocation as Leaflet.LatLngLiteral)
          return
        }
      }
    }

    Geolocation.getCurrentPosition()
      .then((res) => {
        const center: LatLngExpression = {
          lat: res.coords.latitude,
          lng: res.coords.longitude
        }
        if (tenantList && tenantList[activeTenant]) {
          handlerAroundMe(center)
        }
      })
      .catch((error) => {
        console.log(error)
      })
  }

  const handlerAroundMe = (center: Leaflet.LatLngLiteral): void => {
    console.log(center)
    if (mapRef.current) {
      setCurrentUserPos(center)

      // Calculez les coordonnées des coins du rectangle de la zone de 10km autour du centre
      const southWest = L.latLng(center)
        .toBounds(aroundMeDistance ?? 5000)
        .getSouthWest()
      const northEast = L.latLng(center)
        .toBounds(aroundMeDistance ?? 5000)
        .getNorthEast()
      const bounds = L.latLngBounds(southWest, northEast)

      // Utilisez la méthode fitBounds() pour ajuster la vue de la carte à la zone de 10km autour du centre
      mapRef.current.fitBounds(bounds)

      // eslint-disable-next-line prefer-const
      // const zoom = mapRef.current.getZoom()

      if (mapRef.current !== undefined) {
        mapRef.current.eachLayer((l) => {
          if (
            l instanceof L.MarkerClusterGroup &&
            mapRef.current !== undefined
          ) {
            l.disableClustering()
          }
        })
      }

      mapRef.current.on('zoomend', function () {
        if (mapRef.current !== undefined && mapRef.current.getZoom() < 12) {
          if (mapRef.current !== undefined) {
            mapRef.current.eachLayer((l) => {
              if (
                l instanceof L.MarkerClusterGroup &&
                mapRef.current !== undefined
              ) {
                l.enableClustering()
              }
            })
          }
        }
      })
    }
  }

  useEffect(() => {
    const host = document.querySelector('#retorik-shadow')
    const container = document.createElement('div')
    container.id = 'LoaderContainer'
    if (loading && host?.shadowRoot) {
      let loaderContainer = host?.shadowRoot?.querySelector('#LoaderContainer')
      if (loaderContainer !== null) {
      } else {
        host.shadowRoot.append(container)
        loaderContainer = host.shadowRoot.querySelector('#LoaderContainer')
        if (loaderContainer) {
          const root = ReactDOM.createRoot(loaderContainer)
          root.render(CustomLoader)
        }
      }
    }
    if (!loading && host?.shadowRoot) {
      const loaderContainer =
        host?.shadowRoot?.querySelector('#LoaderContainer')
      if (loaderContainer) {
        loaderContainer.remove()
      }
    }
  }, [loading])

  return (
    <div
      className='mapContainer'
      style={
        selectedPOI
          ? ({
              '--pointerColorHightlight': getPoiColor(
                selectedPOI.type as POIType
              )
            } as React.CSSProperties)
          : undefined
      }>
      {selectedPOI && <POIDetail poi={selectedPOI} poiClose={closePoi} />}
      <MapContainer
        center={[46.912815, 3.301331]}
        zoom={9}
        scrollWheelZoom
        whenReady={(): void => {}}
        className='rf-flex rf-h-screen'
        id='mapid'
        ref={(ref): void => {
          if (ref) mapRef.current = ref
        }}
        zoomControl={false}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        />
        <MarkerClusterGroup
          chunkedLoading
          // eslint-disable-next-line react/jsx-no-bind
          iconCreateFunction={(cluster: MarkerCluster): Leaflet.DivIcon => {
            return Leaflet.divIcon({
              html: `<span>${cluster.getChildCount()}</span>`,
              className: 'custom-marker-cluster',
              iconSize: Leaflet.point(33, 33, true)
            })
          }}
          maxClusterRadius={150}
          spiderfyOnMaxZoom>
          {pois &&
            Array.isArray(pois) &&
            pois.length > 0 &&
            pois
              ?.filter(
                (p: POI) =>
                  (p.Location !== undefined &&
                    currentFilter &&
                    currentFilter.some((f) => f === p.type)) ||
                  !currentFilter
              )
              .map((poi: POI, i: number) => (
                <Marker
                  key={i}
                  position={poi.Location || { lat: 0, lng: 0 }}
                  icon={markerDict[poi.type]}
                  ref={(el): any => {
                    if (el)
                      markersRef.current[i] = {
                        marker: el,
                        id: poi.contentItemId
                      }
                  }}
                  eventHandlers={{
                    click: (c: Leaflet.LeafletMouseEvent): void =>
                      markerClickHandler(c, poi)
                  }}
                />
              ))}
        </MarkerClusterGroup>
        <ZoomControl
          key={Date.now()}
          position='bottomright'
          zoomInTitle={
            translation[locale]?.zoomintitle || translation['fr-FR'].zoomintitle
          }
          zoomOutTitle={
            translation[locale]?.zoomouttitle ||
            translation['fr-FR'].zoomouttitle
          }
        />
        {currentUserPos !== undefined && (
          <LocationMarker currentUserPos={currentUserPos} />
        )}
      </MapContainer>
      <button
        id='around-me'
        onClick={(): void => aroundMe()}
        title={`${
          translation[locale]?.aroundme1 || translation['fr-FR'].aroundme1
        } ${aroundMeDistance / 1000} ${
          translation[locale]?.aroundme2 || translation['fr-FR'].aroundme2
        }`}>
        <IonImg
          src={target}
          id='map-target'
          alt='target'
          placeholder={undefined}
        />
      </button>
      <MapFilter settings={mapSettings} />
    </div>
  )
}

const CustomLoader = (
  <div className='loader-container'>
    <div className='loader' aria-hidden='true' />
  </div>
)

export { MapDisplay }

export type { MapProps }
